/**
 * The feed card contract — GET /public/api/v1/feed and /feed/posts/:postId (espotz-backend
 * modules/feedModule/feed.service.ts, GitLab epic 17). The server renders everything, including
 * the caption: clients display it as is.
 */

/** Who a post belongs to — the API's `owner.type`. A brand ad's owner is always `BRAND`. */
export enum FeedOwnerType {
  ORG = 'ORG',
  CLAN = 'CLAN',
  TEAM = 'TEAM',
  ESPOTZ = 'ESPOTZ',
  BRAND = 'BRAND',
}

/** The feed card discriminator — the API's `cardType`. */
export enum FeedCardType {
  TOURNAMENT_EVENT = 'tournament_event',
  SPONSORED = 'sponsored',
}

export type FeedEventType =
  | 'REGISTRATIONS_OPEN'
  | 'REGISTRATION_CLOSING_SOON'
  | 'TOURNAMENT_LIVE'
  | 'WINNERS_DECLARED'
  | 'PRIZE_RELEASED';

export type FeedCtaTarget =
  /** `webPath` is this app's route; `path` the mobile one. */
  | { kind: 'app'; path: string; webPath: string }
  | { kind: 'external'; url: string }
  | { kind: 'comments'; prefill: string };

/** A brand ad's CTA — the button text is `feed.cta.<ACTION>` unless the ad sets its own. */
export const BRAND_CTA_ACTIONS = [
  'LEARN_MORE',
  'SHOP_NOW',
  'PLAY_NOW',
  'SIGN_UP',
  'DOWNLOAD',
  'VISIT_WEBSITE',
] as const;
export type BrandCtaAction = (typeof BRAND_CTA_ACTIONS)[number];

/** Actions on an organic (tournament) post's CTA; the server picks one per viewer and state. */
export enum TournamentCtaAction {
  REGISTER = 'REGISTER',
  REGISTERED = 'REGISTERED',
  SLOTS_FULL = 'SLOTS_FULL',
  REGISTRATIONS_CLOSED = 'REGISTRATIONS_CLOSED',
  WATCH_LIVE = 'WATCH_LIVE',
  VIEW_BRACKET = 'VIEW_BRACKET',
  VIEW_RESULTS = 'VIEW_RESULTS',
  VIEW_TOURNAMENT = 'VIEW_TOURNAMENT',
  BOOST_PRIZE_POOL = 'BOOST_PRIZE_POOL',
  CONGRATULATE = 'CONGRATULATE',
}

export type FeedCtaAction = TournamentCtaAction | BrandCtaAction;

export interface FeedCtaButton {
  action: FeedCtaAction;
  label: string;
  enabled: boolean;
  requiresLogin: boolean;
  target: FeedCtaTarget | null;
}

export interface FeedCta {
  primary: FeedCtaButton;
  secondary: FeedCtaButton | null;
  /** Registration posts while registration is open: the button to show once `at` (ISO) has passed. */
  registrationClose?: { at: string; button: FeedCtaButton };
}

/** An organic post: a tournament moment. */
export interface TournamentFeedCard {
  id: string;
  cardType: FeedCardType.TOURNAMENT_EVENT;
  eventType: FeedEventType;
  /** UTC ISO 8601. */
  createdAt: string;
  /** Who the post belongs to: the organising org, a clan or team, or Espotz itself. */
  owner: {
    type: Exclude<FeedOwnerType, FeedOwnerType.BRAND>;
    id: string | null;
    name: string;
    avatarUrl: string | null;
    slug: string | null;
  };
  location: { region: string | null; country: string | null } | null;
  media: { thumbnailUrl: string | null };
  caption: string;
  likes: FeedLikeState;
  comments: { count: number };
  share: { url: string };
  cta: FeedCta | null;
  tournament: { id: string; name: string };
}

/**
 * One slide's CTA, already resolved by the server (`slide.cta ?? ad.cta`): every slide carries
 * its own, so a swipe swaps the button text and the link together. `label` is the ad's custom
 * button text (shown as is); null = the translated action label.
 */
export interface SponsoredSlideCta {
  action: BrandCtaAction;
  label: string | null;
  url: string;
}

/** One carousel slide: a 4:3 image with its own caption and CTA. */
export interface SponsoredSlide {
  /** Stable across reorders — what events carry, never the index. */
  slideId: string;
  imageUrl: string;
  width: number;
  height: number;
  caption: string;
  cta: SponsoredSlideCta;
}

/**
 * A brand ad in the feed. Only served when the request opts in with
 * `cardTypes=sponsored`, so older builds never see one.
 */
export interface SponsoredFeedCard {
  /** The ad's feed post id: likes, comments, shares and impressions key off it. */
  id: string;
  cardType: FeedCardType.SPONSORED;
  adId: string;
  sponsorship: {
    label: string;
    category: string;
    brand: {
      id: string;
      name: string;
      logoUrl: string | null;
      websiteUrl: string | null;
    };
  };
  owner: { type: FeedOwnerType.BRAND; name: string; avatarUrl: string | null };
  /** 1–10, in display order. */
  slides: SponsoredSlide[];
  likes: FeedLikeState;
  comments: { count: number };
  /** `enabled: false` for restricted categories: no share button at all. */
  share: { enabled: boolean; url: string };
}

/**
 * The brand-ad opt-in for feed reads. Sent as
 * `cardTypes=sponsored&platform=web` plus whichever of `anonId` / `lang` is known.
 */
export interface FeedSponsoredOptIn {
  /** The first-party tracker's install id: the frequency key for a logged-out viewer. */
  anonId: string | null;
  /** The app language (`en`, `ar`, `pt-BR`…): a logged-out viewer's language targeting. */
  lang: string | null;
}

/** What the feed and the single-post endpoint return: a discriminated union on `cardType`. */
export type FeedCard = TournamentFeedCard | SponsoredFeedCard;

/** Picker order. FIRE is the default: a click, and a double-click on the image. */
export const FEED_REACTIONS = ['FIRE', 'GG', 'OP', 'SLAY', 'CLUTCH'] as const;
export type FeedReaction = (typeof FEED_REACTIONS)[number];
export const DEFAULT_FEED_REACTION: FeedReaction = 'FIRE';

export interface FeedLikeState {
  /** Total reactions, every type. */
  count: number;
  likedByMe: boolean;
  myReaction: FeedReaction | null;
  /** Most-used types on the post, most first, at most 3. */
  topReactions: FeedReaction[];
  latestLikerName: string | null;
}

export interface FeedConfig {
  /** Enabled reactions, in picker order. */
  reactions: FeedReaction[];
  defaultReaction: FeedReaction;
}

/** An organic post's owner (a brand ad's owner is always its brand). */
export type FeedOwner = TournamentFeedCard['owner'];

export interface FeedPage {
  posts: FeedCard[];
  nextCursor: string | null;
}

export interface FeedPerson {
  id: string;
  username: string;
  avatarUrl: string | null;
}

export interface FeedComment {
  id: string;
  /** Null when the author deleted their account. */
  author: FeedPerson | null;
  text: string;
  createdAt: string;
  canDelete: boolean;
}

export interface FeedCommentsPage {
  items: FeedComment[];
  nextCursor: string | null;
}

/** One row of the "liked by" list: who reacted, with what. */
export interface FeedLiker extends FeedPerson {
  likedAt: string;
  reaction: FeedReaction;
}

export interface FeedLikesPage {
  items: FeedLiker[];
  nextCursor: string | null;
}

export type FeedShareChannel =
  'instagram' | 'whatsapp' | 'facebook' | 'copy_link' | 'other';

/** Same cap as the API (`FEED_COMMENT_MAX_LENGTH`). */
export const FEED_COMMENT_MAX_LENGTH = 300;
